import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Users, Building2, Shield, UserPlus, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import {
  addAssignmentGroupMember,
  createAssignmentGroup,
  deleteAssignmentGroup,
  getAssignmentGroupMembers,
  getAssignmentGroups,
  removeAssignmentGroupMember,
  updateAssignmentGroup,
} from '../api/assignmentGroups';
import { getTenantTicketAccessOptions, getUsers } from '../api/users';

const EMPTY_FORM = {
  tenant_id: '',
  name: '',
  description: '',
  email: '',
};

const SUPPORT_TEAM_TEMPLATE = {
  name: 'IT Support Team',
  description: 'Primary support team for ticket assignment and ownership.',
  email: 'support@cantiktech.com',
};

const SuperAdminAssignmentGroups = () => {
  const [groups, setGroups] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [groupMembers, setGroupMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [newMemberUserId, setNewMemberUserId] = useState('');
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const selectedGroup = useMemo(
    () => groups.find((g) => String(g.id) === String(selectedGroupId)) || null,
    [groups, selectedGroupId]
  );

  const eligibleUsers = useMemo(() => {
    if (!selectedGroup) return [];
    const memberIds = new Set(groupMembers.map((m) => String(m.user_id)));
    return users
      .filter(
      (u) => {
        const role = String(u.role || '').trim().toLowerCase();
        const roleAllowed = ['admin', 'employee', 'global_support', 'super_admin'].includes(role);
        return roleAllowed && !memberIds.has(String(u.id));
      }
    )
      .sort((a, b) => {
        const byName = String(a.name || '').localeCompare(String(b.name || ''));
        if (byName !== 0) return byName;
        return String(a.email || '').localeCompare(String(b.email || ''));
      });
  }, [users, selectedGroup, groupMembers]);

  useEffect(() => {
    setNewMemberUserId('');
  }, [selectedGroupId]);

  const loadGroups = useCallback(async () => {
    const res = await getAssignmentGroups();
    const rows = Array.isArray(res?.data) ? res.data : [];
    setGroups(rows);
    if (!selectedGroupId && rows.length) {
      setSelectedGroupId(rows[0].id);
    } else if (selectedGroupId && !rows.some((g) => String(g.id) === String(selectedGroupId))) {
      setSelectedGroupId(rows[0]?.id || null);
    }
  }, [selectedGroupId]);

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const [groupRes, tenantRes, userRes] = await Promise.all([
        getAssignmentGroups(),
        getTenantTicketAccessOptions(),
        getUsers(),
      ]);
      const groupRows = Array.isArray(groupRes?.data) ? groupRes.data : [];
      setGroups(groupRows);
      if (groupRows.length) {
        setSelectedGroupId(groupRows[0].id);
      }
      setTenants(Array.isArray(tenantRes?.data) ? tenantRes.data : []);
      setUsers(Array.isArray(userRes?.data) ? userRes.data : []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load assignment group data');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMembers = useCallback(async () => {
    if (!selectedGroupId) {
      setGroupMembers([]);
      return;
    }
    try {
      const res = await getAssignmentGroupMembers(selectedGroupId);
      setGroupMembers(Array.isArray(res?.data) ? res.data : []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load members');
      setGroupMembers([]);
    }
  }, [selectedGroupId]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!form.tenant_id || !form.name.trim()) {
      toast.error('Tenant and group name are required');
      return;
    }
    try {
      setCreating(true);
      await createAssignmentGroup({
        tenant_id: Number(form.tenant_id),
        name: form.name.trim(),
        description: form.description.trim() || null,
        email: form.email.trim() || null,
      });
      toast.success('Assignment group created');
      setForm(EMPTY_FORM);
      await loadGroups();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create assignment group');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!deleteTargetId) return;
    try {
      await deleteAssignmentGroup(deleteTargetId);
      toast.success('Assignment group deleted');
      setDeleteTargetId(null);
      await loadGroups();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete assignment group');
    }
  };

  const handleOpenEditModal = (group) => {
    setEditingGroup({
      id: group.id,
      name: group.name || '',
      description: group.description || '',
      email: group.email || '',
      is_active: group.is_active === true || group.is_active === 1 || String(group.is_active) === '1',
    });
  };

  const handleSaveEditGroup = async () => {
    if (!editingGroup?.id) return;
    if (!editingGroup.name.trim()) {
      toast.error('Group name is required');
      return;
    }
    try {
      setSavingEdit(true);
      await updateAssignmentGroup(editingGroup.id, {
        name: editingGroup.name.trim(),
        description: editingGroup.description.trim() || null,
        email: editingGroup.email.trim() || null,
        is_active: !!editingGroup.is_active,
      });
      toast.success('Assignment group updated');
      setEditingGroup(null);
      await loadGroups();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update assignment group');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleAddMember = async () => {
    if (!selectedGroupId || !newMemberUserId) {
      toast.error('Select a user first');
      return;
    }
    try {
      setAddingMember(true);
      await addAssignmentGroupMember(selectedGroupId, { user_id: Number(newMemberUserId) });
      toast.success('Member added');
      setNewMemberUserId('');
      await loadMembers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add member');
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    try {
      await removeAssignmentGroupMember(selectedGroupId, userId);
      toast.success('Member removed');
      await loadMembers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to remove member');
    }
  };

  return (
    <DashboardLayout userRole="super_admin">
      <div className="space-y-6 pb-2">
        <div className="rounded-3xl tenant-brand-gradient text-white p-6 md:p-7 shadow-xl shadow-indigo-500/20 border border-white/15">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-white/20 border border-white/30 flex items-center justify-center backdrop-blur">
                <Shield className="w-7 h-7" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Assignment Group Control</h1>
                <p className="text-white/80 text-sm mt-1">
                  Create groups, assign employees, and manage support ownership by tenant.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-xl bg-white/15 px-3 py-2 border border-white/20">
                <p className="text-[11px] uppercase tracking-wide text-white/70">Groups</p>
                <p className="text-lg font-bold">{groups.length}</p>
              </div>
              <div className="rounded-xl bg-white/15 px-3 py-2 border border-white/20">
                <p className="text-[11px] uppercase tracking-wide text-white/70">Tenants</p>
                <p className="text-lg font-bold">{tenants.length}</p>
              </div>
              <div className="rounded-xl bg-white/15 px-3 py-2 border border-white/20">
                <p className="text-[11px] uppercase tracking-wide text-white/70">Members</p>
                <p className="text-lg font-bold">{groupMembers.length}</p>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl p-10 text-center text-slate-500 shadow-sm">
            Loading assignment group settings...
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
            <div className="xl:col-span-1 bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 p-5 md:p-6 shadow-sm">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2 tracking-tight">
                <Plus className="w-5 h-5 text-indigo-500" />
                Create Assignment Group
              </h2>
              <p className="text-xs text-slate-500 mb-3">
                Build tenant-wise support teams and route tickets to the right group.
              </p>
              <button
                type="button"
                onClick={() =>
                  setForm((prev) => ({
                    ...prev,
                    name: SUPPORT_TEAM_TEMPLATE.name,
                    description: SUPPORT_TEAM_TEMPLATE.description,
                    email: SUPPORT_TEAM_TEMPLATE.email,
                  }))
                }
                className="mb-3 w-full rounded-xl border border-indigo-200 dark:border-indigo-700 px-3 py-2 text-xs font-semibold text-indigo-700 dark:text-indigo-300 bg-indigo-50/70 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30"
              >
                Use Support Team Template
              </button>
              <form onSubmit={handleCreateGroup} className="space-y-3">
                <select
                  value={form.tenant_id}
                  onChange={(e) => setForm((prev) => ({ ...prev, tenant_id: e.target.value }))}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm shadow-sm"
                  required
                >
                    <option value="">Select Tenant</option>
                  {tenants.map((tenant) => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </option>
                  ))}
                </select>
                <input
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Group name (e.g. IT Support Team)"
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm shadow-sm"
                  required
                />
                <input
                  value={form.description}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="Description (optional)"
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm shadow-sm"
                />
                <input
                  value={form.email}
                  onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                  placeholder="Group email (optional)"
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm shadow-sm"
                />
                <button
                  type="submit"
                  disabled={creating}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-60 shadow-md shadow-indigo-500/20"
                >
                  <Plus className="w-4 h-4" />
                  {creating ? 'Creating...' : 'Create Support Group'}
                </button>
              </form>
            </div>

            <div className="xl:col-span-2 bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 p-5 md:p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">Assignment Groups</h2>
                <span className="text-xs font-semibold text-slate-500">{groups.length} total</span>
              </div>
              <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1">
                {groups.map((group) => (
                  <button
                    key={group.id}
                    type="button"
                    onClick={() => setSelectedGroupId(group.id)}
                    className={`w-full text-left rounded-2xl border px-4 py-3 transition-all ${
                      String(selectedGroupId) === String(group.id)
                        ? 'border-indigo-400 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 shadow-sm'
                        : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/30 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-900 dark:text-white text-sm">{group.name}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{group.description || 'No description'}</p>
                        <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
                          <Building2 className="w-3 h-3" />
                          Tenant ID: {group.tenant_id}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEditModal(group);
                          }}
                          className="p-2 text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg"
                          title="Edit group"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTargetId(group.id);
                          }}
                          className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                          title="Delete group"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </button>
                ))}
                {!groups.length && (
                  <p className="text-sm text-slate-500">No assignment groups created yet.</p>
                )}
              </div>
            </div>

            <div className="xl:col-span-3 bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 p-5 md:p-6 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-indigo-500" />
                  Support Team Members {selectedGroup ? `- ${selectedGroup.name}` : ''}
                </h2>
                <p className="text-xs text-slate-500">
                  Add employees/admins to this group so they can receive and handle assigned tickets.
                </p>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <select
                    value={newMemberUserId}
                    onChange={(e) => setNewMemberUserId(e.target.value)}
                    className="min-w-[260px] rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm shadow-sm"
                    disabled={!selectedGroup || eligibleUsers.length === 0}
                  >
                    <option value="">Select employee/admin to add</option>
                    {eligibleUsers.length === 0 && selectedGroup ? (
                      <option value="" disabled>
                        No eligible users found for this tenant
                      </option>
                    ) : null}
                    {eligibleUsers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} ({u.email})
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleAddMember}
                    disabled={!selectedGroup || !newMemberUserId || addingMember}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-60 shadow-md shadow-emerald-500/20"
                  >
                    <UserPlus className="w-4 h-4" />
                    {addingMember ? 'Adding...' : 'Add Member'}
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-slate-700">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50/70 dark:bg-slate-900/40">
                    <tr className="border-b border-slate-200 dark:border-slate-700">
                      <th className="text-left py-2 pr-3 text-slate-500 font-semibold">Name</th>
                      <th className="text-left py-2 pr-3 text-slate-500 font-semibold">Email</th>
                      <th className="text-left py-2 pr-3 text-slate-500 font-semibold">User Role</th>
                      <th className="text-right py-2 text-slate-500 font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupMembers.map((member) => (
                      <tr key={member.user_id} className="border-b border-slate-100 dark:border-slate-700/50 bg-white dark:bg-slate-800">
                        <td className="py-2 pr-3 text-slate-900 dark:text-white font-medium">{member.name}</td>
                        <td className="py-2 pr-3 text-slate-600 dark:text-slate-300">{member.email}</td>
                        <td className="py-2 pr-3 text-slate-600 dark:text-slate-300 uppercase text-xs">{member.user_role}</td>
                        <td className="py-2 text-right">
                          <button
                            type="button"
                            onClick={() => handleRemoveMember(member.user_id)}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!groupMembers.length && (
                      <tr>
                        <td colSpan={4} className="py-4 text-center text-slate-500">
                          {selectedGroup ? 'No members in this group yet.' : 'Select a group to manage members.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {deleteTargetId && (
        <div className="fixed inset-0 z-[120] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-3xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-2xl p-5 space-y-4">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Delete assignment group?</h3>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              This will remove the group and its member mapping.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTargetId(null)}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-100 dark:bg-slate-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteGroup}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {editingGroup && (
        <div className="fixed inset-0 z-[120] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-3xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-2xl p-5 space-y-4">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Edit assignment group</h3>
            <div className="space-y-3">
              <input
                value={editingGroup.name}
                onChange={(e) => setEditingGroup((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Group name"
                className="w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm shadow-sm"
              />
              <input
                value={editingGroup.description}
                onChange={(e) => setEditingGroup((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Description"
                className="w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm shadow-sm"
              />
              <input
                value={editingGroup.email}
                onChange={(e) => setEditingGroup((prev) => ({ ...prev, email: e.target.value }))}
                placeholder="Group email"
                className="w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm shadow-sm"
              />
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  checked={!!editingGroup.is_active}
                  onChange={(e) => setEditingGroup((prev) => ({ ...prev, is_active: e.target.checked }))}
                  className="rounded border-slate-300"
                />
                <span>Active group</span>
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditingGroup(null)}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-100 dark:bg-slate-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEditGroup}
                disabled={savingEdit}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
              >
                {savingEdit ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default SuperAdminAssignmentGroups;
